import { useId, type ReactNode } from "react";
import { Icon, type IconName } from "@omnabase/ui";

/** The form's own settings as rows under the fields (builder v2, the founder's design: "Form" with Tags, Where submissions go, Embed in your
 *  page, Share; the form editor round adds Texts after Tags). Each row is a toggle named by its title, with a short summary of its state beside it (`lib/formSummaries.ts`), and opens in
 *  place into the region of that name; one form row is open at a time (apart from the open field). */
export type FormRowId = "tags" | "texts" | "where" | "embed" | "share";
export type FormRowDef = { id: FormRowId; title: string; icon: IconName; summary: string; body: ReactNode };

export function FormRows({ rows, open, onToggle }: { rows: readonly FormRowDef[]; open: FormRowId | null; onToggle: (id: FormRowId) => void }) {
  return (
    <div className="ob-form-builder__form-rows">
      <div className="ob-form-builder__eyebrow"><span className="ob-form-builder__eyebrow-text">Form</span></div>
      {rows.map((r) => <FormRow key={r.id} row={r} open={r.id === open} onToggle={() => onToggle(r.id)} />)}
    </div>
  );
}

function FormRow({ row, open, onToggle }: { row: FormRowDef; open: boolean; onToggle: () => void }) {
  const id = useId();
  return (
    <div className={`ob-form-builder__row${open ? " ob-form-builder__row--open" : ""}`}>
      <div className="ob-form-builder__row-head">
        <button type="button" className="ob-form-builder__toggle-row" aria-expanded={open} aria-controls={open ? `${id}-body` : undefined} aria-labelledby={`${id}-title`} aria-describedby={`${id}-summary`} onClick={onToggle}>
          <span className="ob-form-builder__mark"><Icon name={row.icon} size="sm" /></span>
          <span className="ob-form-builder__row-text"><span className="ob-form-builder__row-title" id={`${id}-title`}>{row.title}</span><span className="ob-form-builder__row-line" id={`${id}-summary`}>{row.summary}</span></span>
          <Icon name={open ? "chevronUp" : "chevronDown"} size="sm" className="ob-form-builder__chevron" />
        </button>
      </div>{" " /* the summary and the open body read as two phrases, never one word */}
      {open && <div className="ob-form-builder__row-body" role="region" id={`${id}-body`} aria-labelledby={`${id}-title`}>{row.body}</div>}
    </div>
  );
}
